/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { ReactComponent as LogoSeteukPro } from "assets/img/logo/logo-seteuk-pro.svg";
import BaseButton from "components/common/Button/Base";
import TermDialog, { TermType } from "components/common/Dialog/Term";
import BaseInput from "components/common/Input/Base";
import { LoginInfo, SignUpData } from "models/user.model";
import {
  checkDeviceVeriCode,
  registerUser,
  sendAuthCodeOnDevice,
  sendLoginRequest,
  signUpAttachFile,
} from "pages/remotes";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE } from "styles/base/variables";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "components/common/Button/Icon";
import { EMAIL_REG_EX, RequestState } from "data/user.data";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";

type UserInfoIdType =
  | "email"
  | "verification"
  | "password"
  | "passwordConfirm"
  | "name"
  | "taxAgentNumber"
  | "taxOfficeCertFile"
  | "mobileNo"
  | "mobileVeriCode"
  | "businessName"
  | "businessRegNumber"
  | "businessCeoName"
  | "businessType"
  | "taxType"
  | "taxEmail"
  | "salaryMonth"
  | "salaryDay";

type UserInfoType = {
  [key in UserInfoIdType]: string;
};

type DeviceType = "email" | "mobile";

interface InputPropsProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: "filled" | "outlined" | "standard";
  css?: ReturnType<typeof css>;
}

interface FormInputProps {
  id:
    | UserInfoIdType
    | "bt-corporate"
    | "bt-private"
    | "tt-normal"
    | "tt-simple"
    | "tt-free";
  type: "text" | "email" | "password" | "tel" | "file" | "radio";
  name?: "businessType" | "taxType";
  label?: string;
  labelCss?: string;
  placeholder?: string;
  value?: string;
  infoState?: RequestState;
  disabled?: boolean;
  inputProps?: InputPropsProps;
  inputHandler?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  button?: {
    label: string;
    disabled?: boolean;
    action: () => void;
  };
}

const inputRadioCss = css`
  margin: 0;

  &:checked {
    accent-color: ${PALETTE.primary};
  }
`;

const infoRegEx: { [key in UserInfoIdType]: RegExp } = {
  email: EMAIL_REG_EX,
  verification: /^\d{6}$/,
  password:
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,20}$/,
  passwordConfirm:
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~])[A-Za-z\\d!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~]{8,20}$/, // app.smartcta 비밀번호 로직
  name: /^[A-Za-z가-힣]{2,8}$/,
  taxAgentNumber: /^\d{4,8}$/, // 접두 알파벳이 있어 적용하지 않음 (입력 패턴 다양)
  taxOfficeCertFile: /^.*\.(pdf|jpg|jpeg|png)$/,
  mobileNo: /^01[0-9]{8,9}$/,
  mobileVeriCode: /^\d{6}$/,
  businessName: /^.{2,20}$/,
  businessRegNumber: /^\d{10}$/,
  businessCeoName: /^[가-힣]{2,5}$/,
  businessType: /^(corporate|private)$/,
  taxType: /^(normal|simple|free)$/,
  taxEmail: EMAIL_REG_EX,
  salaryMonth: /^(0[1-9]|1[0-2])$/,
  salaryDay: /^(0[1-9]|[12][0-9]|3[01])$/,
};

const dashPatternRegEx: { [key in UserInfoIdType]: RegExp } = {
  ...infoRegEx,
  businessRegNumber: /^(\d{3})-?(\d{2})-?(\d{5})$/,
  mobileNo: /^(\d{3})-?(\d{3,4})-?(\d{4})$/,
};

function SignUp() {
  const navigate = useNavigate();

  const [veriData, setVeriData] = useState<{
    [key in DeviceType]: { id: string; code: string };
  }>({
    email: { id: "", code: "" },
    mobile: { id: "", code: "" },
  });
  const [infoState, setInfoState] = useState<{
    [key in UserInfoIdType]: RequestState;
  }>({
    email: { errMsg: "", isValid: false },
    verification: { errMsg: "", isValid: false },
    password: { errMsg: "", isValid: false },
    passwordConfirm: { errMsg: "", isValid: false },
    name: { errMsg: "", isValid: false },
    taxAgentNumber: { errMsg: "", isValid: false },
    taxOfficeCertFile: { errMsg: "", isValid: false },
    mobileNo: { errMsg: "", isValid: false },
    mobileVeriCode: { errMsg: "", isValid: false },
    businessName: { errMsg: "", isValid: false },
    businessRegNumber: { errMsg: "", isValid: false },
    businessCeoName: { errMsg: "", isValid: false },
    businessType: { errMsg: "", isValid: true },
    taxType: { errMsg: "", isValid: true },
    taxEmail: { errMsg: "", isValid: false },
    salaryMonth: { errMsg: "", isValid: true },
    salaryDay: { errMsg: "", isValid: true },
  });
  const [userInfo, setUserInfo] = useState<UserInfoType>({
    email: "",
    verification: "",
    password: "",
    passwordConfirm: "",
    name: "",
    taxAgentNumber: "",
    taxOfficeCertFile: "",
    mobileNo: "",
    mobileVeriCode: "",
    businessName: "",
    businessRegNumber: "",
    businessCeoName: "",
    businessType: "corporate",
    taxType: "normal",
    taxEmail: "",
    salaryMonth: "THIS_MONTH",
    salaryDay: new Date().getDate().toString(),
  });
  const [termDialogOpen, setTermDialogOpen] = useState<TermType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
    type: "error",
    confirmFunc: () => {},
  });

  useEffect(() => {
    handleValidity("email", "올바른 이메일 주소를 입력해주세요.");
    if (veriData.email.id)
      setVeriData({ ...veriData, email: { id: "", code: "" } }); // 인증했던 이메일 정보가 달라지면 초기화
  }, [userInfo.email]);

  useEffect(() => {
    handleValidity("verification", "6자리 인증번호를 입력해주세요.");
  }, [userInfo.verification]);

  useEffect(() => {
    handleValidity("password", "영문 대소문자, 숫자, 특수문자 포함 8~20자");
  }, [userInfo.password]);

  useEffect(() => {
    handlePasswordConfirmValidity();
  }, [userInfo.passwordConfirm]);

  useEffect(() => {
    if (userInfo.name)
      setInfoState({
        ...infoState,
        name: { ...infoState.name, isValid: true },
      });
    else
      setInfoState({
        ...infoState,
        name: { ...infoState.name, isValid: false },
      });
  }, [userInfo.name]);

  useEffect(() => {
    if (userInfo.taxAgentNumber)
      setInfoState({
        ...infoState,
        taxAgentNumber: { ...infoState.taxAgentNumber, isValid: true },
      });
    else
      setInfoState({
        ...infoState,
        taxAgentNumber: { ...infoState.taxAgentNumber, isValid: false },
      });
  }, [userInfo.taxAgentNumber]);

  useEffect(() => {
    if (userInfo.taxOfficeCertFile)
      setInfoState({
        ...infoState,
        taxOfficeCertFile: { ...infoState.taxOfficeCertFile, isValid: true },
      });
    else
      setInfoState({
        ...infoState,
        taxOfficeCertFile: { ...infoState.taxOfficeCertFile, isValid: false },
      });
  }, [userInfo.taxOfficeCertFile]);

  useEffect(() => {
    const mobileNo = userInfo.mobileNo;
    if (mobileNo.includes("-"))
      handleValidity("mobileNo", "올바른 휴대폰번호를 입력해주세요.", true);
    else handleValidity("mobileNo", "올바른 휴대폰번호를 입력해주세요.");

    if (veriData.mobile.id)
      setVeriData({ ...veriData, mobile: { id: "", code: "" } }); // 인증했던 휴대폰 정보가 달라지면 초기화
  }, [userInfo.mobileNo]);

  useEffect(() => {
    handleValidity("mobileVeriCode", "6자리 인증번호를 입력해주세요.");
  }, [userInfo.mobileVeriCode]);

  useEffect(() => {
    if (userInfo.businessName)
      setInfoState({
        ...infoState,
        businessName: { ...infoState.businessName, isValid: true },
      });
    else
      setInfoState({
        ...infoState,
        businessName: { ...infoState.businessName, isValid: false },
      });
  }, [userInfo.businessName]);

  useEffect(() => {
    const regNum = userInfo.businessRegNumber;
    if (regNum.includes("-"))
      handleValidity(
        "businessRegNumber",
        "사업자등록번호 10자리를 입력해주세요.",
        true
      );
    else
      handleValidity(
        "businessRegNumber",
        "사업자등록번호 10자리를 입력해주세요."
      );
  }, [userInfo.businessRegNumber]);

  useEffect(() => {
    if (userInfo.businessCeoName)
      setInfoState({
        ...infoState,
        businessCeoName: { ...infoState.businessCeoName, isValid: true },
      });
    else
      setInfoState({
        ...infoState,
        businessCeoName: { ...infoState.businessCeoName, isValid: false },
      });
  }, [userInfo.businessCeoName]);

  useEffect(() => {
    const currBizType = userInfo.businessType;
    const currTaxType = userInfo.taxType;
    if (currBizType === "corporate" && currTaxType === "simple") {
      const tmpUserInfo = { ...userInfo };
      tmpUserInfo.taxType = "normal";
      setUserInfo(tmpUserInfo);
    }
  }, [userInfo.businessType]);

  useEffect(() => {
    handleValidity("taxEmail", "올바른 이메일 주소를 입력해주세요.");
  }, [userInfo.taxEmail]);

  const toLogin = () => {
    navigate("/login");
  };

  const userInfoHanlder = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, name, value } = e.target;

    // 현재 radio 타입만 name이 존재
    if (name) {
      setUserInfo({
        ...userInfo,
        [name]: value,
      });
      return;
    } else {
      setUserInfo({
        ...userInfo,
        [id]: value,
      });
    }
  };

  const handleValidity = (
    id: UserInfoIdType,
    errMsg: string,
    isDashed: boolean = false
  ) => {
    const veriValue = userInfo[id];
    const veriWasValid = infoState[id].isValid;
    const tmpInfoState = { ...infoState };
    if (veriValue) {
      const regExp = isDashed ? dashPatternRegEx[id] : infoRegEx[id];
      if (regExp.test(veriValue)) {
        if (!veriWasValid) tmpInfoState[id].isValid = true;
        tmpInfoState[id].errMsg = "";
      } else {
        if (veriWasValid) tmpInfoState[id].isValid = false;
        tmpInfoState[id].errMsg = errMsg;
      }
    } else {
      if (veriWasValid) tmpInfoState[id].isValid = false;
      tmpInfoState[id].errMsg = "";
    }
    setInfoState(tmpInfoState);
  };

  const handlePasswordConfirmValidity = () => {
    const pwValue = userInfo.password;
    const pwcValue = userInfo.passwordConfirm;
    const pwcWasValid = infoState.passwordConfirm.isValid;
    const tmpInfoState = { ...infoState };
    if (pwValue === pwcValue) {
      if (!pwcWasValid) tmpInfoState.passwordConfirm.isValid = true;
      tmpInfoState.passwordConfirm.errMsg = "";
    } else {
      if (pwcWasValid) tmpInfoState.passwordConfirm.isValid = false;
      tmpInfoState.passwordConfirm.errMsg = "비밀번호가 일치하지 않습니다.";
    }
    setInfoState(tmpInfoState);
  };

  const requestVerification = (
    device: DeviceType,
    veriId: "verification" | "mobileVeriCode",
    targetId: "email" | "mobileNo"
  ) => {
    setInfoState({
      ...infoState,
      [targetId]: {
        ...infoState[targetId],
        isLoading: true,
        errMsg: "인증요청중입니다.",
      },
    });
    sendAuthCodeOnDevice(device, userInfo[targetId]).then(
      (res) => {
        const tmpInfoState = { ...infoState };
        tmpInfoState[targetId].isLoading = false;

        const veriDataId = res?.data.id;
        if (veriDataId) {
          setVeriData({ ...veriData, [device]: { id: veriDataId, code: "" } });

          tmpInfoState[targetId].errMsg = "";
          tmpInfoState[targetId].isValid = true;
          tmpInfoState[veriId].errMsg = "";
        }
        setInfoState(tmpInfoState);
      },
      (err) => {
        const errMsg =
          err.response?.data?.message || "인증번호 발송에 실패했습니다.";

        const tmpInfoState = { ...infoState };
        tmpInfoState.email.isLoading = false;

        // 에러 메세지가 너무 길 경우 alert로 표시
        if (errMsg.length > 20) {
          alert(errMsg);
        } else {
          tmpInfoState[targetId].errMsg = errMsg;
        }

        setInfoState(tmpInfoState);
      }
    );
  };

  const fileHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.id as UserInfoIdType;
    const file = e.target.files?.[0];
    if (!file) return;

    const tmpUserInfo = { ...userInfo };
    const tmpInfoState = { ...infoState };
    const fileSize = file.size / 1024 / 1024;
    if (fileSize > 10) {
      tmpInfoState[id].errMsg = "10MB 이하의 파일만 첨부 가능합니다.";
      setInfoState(tmpInfoState);
      return;
    }

    if (!userInfo.email) {
      tmpInfoState[id].errMsg = "이메일을 먼저 입력해주세요.";
      setInfoState(tmpInfoState);
      return;
    }

    if (!(veriData.email.id && veriData.email.code)) {
      tmpInfoState[id].errMsg = "이메일 인증을 먼저 해주세요.";
      setInfoState(tmpInfoState);
      return;
    }

    const formData: FormData = new FormData();
    formData.append("file", file);
    formData.append("fileName", "세무사등록증");
    formData.append("codeId", veriData.email.id);
    formData.append("code", veriData.email.code);
    formData.append("email", userInfo.email);

    signUpAttachFile(formData)
      .then((res) => {
        tmpUserInfo[id] = file.name;
        setUserInfo(tmpUserInfo);
        tmpInfoState[id].errMsg = "";
        setInfoState(tmpInfoState);
      })
      .catch((err) => {
        tmpInfoState[id].errMsg = "파일 첨부에 실패했습니다.";
        setInfoState(tmpInfoState);
      });
  };

  const checkVeriCode = (
    device: DeviceType,
    veriId: "verification" | "mobileVeriCode",
    targetId: "email" | "mobileNo"
  ) => {
    const tmpInfoState = { ...infoState };

    if (!veriData[device].id) {
      tmpInfoState[veriId].errMsg = "인증번호를 먼저 요청해주세요.";
      setInfoState(tmpInfoState);
      return;
    }

    tmpInfoState[veriId].isLoading = true;
    tmpInfoState[veriId].errMsg = "인증번호 확인중입니다.";
    setInfoState(tmpInfoState);

    checkDeviceVeriCode(device, veriData[device].id, userInfo[veriId]).then(
      (res) => {
        tmpInfoState[targetId].isDisabled = true;
        tmpInfoState[veriId] = {
          errMsg: "",
          isValid: true,
          isLoading: false,
          isDisabled: true,
        };
        setInfoState(tmpInfoState);
        setVeriData({
          ...veriData,
          [device]: {
            ...veriData[device],
            code: userInfo[veriId],
          },
        });
      },
      (err) => {
        const errMsg =
          err.response?.data?.message || "인증번호 검증에 실패했습니다.";
        setInfoState({
          ...infoState,
          [veriId]: { ...infoState[targetId], isLoading: false, errMsg },
        });
      }
    );
  };

  const deleteCertFileRecord = () => {
    const tmpUserInfo = { ...userInfo };
    const tmpInfoState = { ...infoState };
    tmpUserInfo.taxOfficeCertFile = "";
    tmpInfoState.taxOfficeCertFile.errMsg = "";
    setUserInfo(tmpUserInfo);
    setInfoState(tmpInfoState);
  };

  const openTermDialog = (term: TermType) => {
    setTermDialogOpen(term);
  };

  const checkAllValid = () => {
    const infoStateKeys = Object.keys(infoState) as UserInfoIdType[];
    const isAllValid = infoStateKeys.every((key) => infoState[key].isValid);
    return isAllValid;
  };

  const convertButtonText = (
    infoState: { [key in UserInfoIdType]: RequestState },
    isLoading: boolean
  ) => {
    if (isLoading) return "회원가입 중...";
    if (!(infoState.email.isValid && infoState.verification.isValid))
      return "이메일 인증을 완료해주세요";
    if (!(infoState.password.isValid && infoState.passwordConfirm.isValid))
      return "비밀번호를 확인해주세요";
    if (!infoState.name.isValid) return "이름을 입력해주세요";
    if (!infoState.taxAgentNumber.isValid) return "세무사 번호를 입력해주세요";
    if (!infoState.taxOfficeCertFile.isValid)
      return "세무사 등록증을 첨부해주세요";
    if (!(infoState.mobileNo.isValid && infoState.mobileVeriCode.isValid))
      return "휴대폰 인증을 완료해주세요";
    if (!infoState.businessName.isValid) return "업체명을 입력해주세요";
    if (!infoState.businessRegNumber.isValid)
      return "사업자등록번호를 입력해주세요";
    if (!infoState.businessCeoName.isValid) return "대표자명을 입력해주세요";
    if (!infoState.taxEmail.isValid) return "세무사 이메일을 입력해주세요";
    if (
      userInfo.businessType === "corporate" &&
      userInfo.taxType === "simple"
    ) {
      if (!infoState.salaryMonth.isValid) return "급여월을 선택해주세요";
      if (!infoState.salaryDay.isValid) return "급여일을 선택해주세요";
    }

    return "회원가입";
  };

  const submitSignup = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formTarget = e.target as HTMLFormElement;

    const signUpData: SignUpData = {
      email: formTarget.email.value,
      password: formTarget.password.value,
      name: userInfo.name,
      mobileNo: userInfo.mobileNo?.replace(/-/g, ""),
      taxAgentNumber: userInfo.taxAgentNumber?.replace(/-/g, ""),
      origin: "TAX_OFFICE",
      organization: {
        name: userInfo.businessName,
      },
      businesses: [
        {
          regNumber: userInfo.businessRegNumber?.replace(/-/g, ""),
          name: userInfo.businessName,
          ceoName: userInfo.businessCeoName,
          productId: "630c20f03974b51d0a6f2fee", // 어떤 값인지 확인 필요
          isPrivate: userInfo.businessType === "private",
          isSimplifiedTaxBusiness: userInfo.taxType === "simple",
          isVatFreeTaxBusiness: userInfo.taxType === "free",
        },
      ],
      verifications: {
        email: {
          verificationCode: veriData.email.code,
          verificationId: veriData.email.id,
        },
        mobile: {
          verificationCode: veriData.mobile.code,
          verificationId: veriData.mobile.id,
        },
      },
    };

    setIsLoading(true);
    registerUser(signUpData)
      .then((res) => {
        setIsLoading(false);
        const loginInfo: LoginInfo = {
          email: signUpData.email,
          password: signUpData.password,
        };

        sendLoginRequest(loginInfo).then(
          (res) => {
            const tmpAlertData = { ...alertDialogData };
            tmpAlertData.message = "회원가입이 완료되었습니다.";
            tmpAlertData.type = "success";
            tmpAlertData.confirmFunc = () => {
              setIsAlertOpen(false);
              navigate("/card");
            };
            setAlertDialogData(tmpAlertData);
            setIsAlertOpen(true);
          },
          (err) => {
            console.error(err);
            const tmpAlertData = { ...alertDialogData };
            tmpAlertData.message =
              err.response?.data?.message || "로그인에 실패했습니다.";
            tmpAlertData.type = "error";
            tmpAlertData.confirmFunc = () => {
              setIsAlertOpen(false);
              navigate("/login");
            };
            setAlertDialogData(tmpAlertData);
            setIsAlertOpen(true);
          }
        );
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.message =
          err.response?.data?.message || "회원가입에 실패했습니다.";
        tmpAlertData.type = "error";
        tmpAlertData.confirmFunc = () => {
          setIsAlertOpen(false);
        };
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      });
  };

  return (
    <div
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
    >
      <div
        css={css`
          margin: 48px;
          padding: 36px;
          background-color: ${PALETTE.light};
          border-radius: 24px;
          display: flex;
          flex-direction: column;
          position: relative;
        `}
      >
        <div
          css={css`
            position: absolute;
            top: -36px;
            right: 4px;
          `}
        >
          <BaseButton
            variant="text"
            color={PALETTE.primary}
            css={css`
              background-color: transparent;
              font-weight: 600;
            `}
            onClick={toLogin}
          >
            <span
              css={css`
                margin-right: 8px;
              `}
            >
              {"<"}
            </span>
            로그인
          </BaseButton>
        </div>
        <div
          css={css`
            margin-bottom: 24px;
            display: flex;
            justify-content: center;
          `}
        >
          <LogoSeteukPro width="160px" height="fit-content" />
        </div>

        <h1
          css={css`
            margin: 24px 0;
            font-size: 20px;
          `}
        >
          회원가입
        </h1>

        <form
          css={css`
            display: flex;
            flex-direction: column;
            gap: 32px;
          `}
          onSubmit={(e) => submitSignup(e)}
        >
          <FormSection title="기본정보">
            <FormRow>
              <FormInput
                id="email"
                type="email"
                placeholder="이메일"
                disabled={infoState.email.isLoading}
                value={userInfo.email}
                infoState={infoState.email}
                inputHandler={userInfoHanlder}
                inputProps={{
                  css: css`
                    width: 200px;
                  `,
                }}
                button={{
                  label: "인증요청",
                  disabled:
                    !infoState.email.isValid ||
                    infoState.email.isLoading ||
                    Boolean(veriData.email.id),
                  action: () =>
                    requestVerification("email", "verification", "email"),
                }}
              />
              <FormInput
                id="verification"
                type="text"
                placeholder="인증번호"
                value={userInfo.verification}
                infoState={infoState.verification}
                disabled={!veriData.email.id}
                inputHandler={userInfoHanlder}
                inputProps={{
                  maxLength: 6,
                  css: css`
                    width: 96px;
                  `,
                }}
                button={{
                  label: "인증확인",
                  disabled: !(
                    veriData.email.id && infoState.verification.isValid
                  ),
                  action: () => checkVeriCode("email", "verification", "email"),
                }}
              />
            </FormRow>
            <FormRow>
              <FormInput
                id="password"
                type="password"
                placeholder="비밀번호"
                infoState={infoState.password}
                inputHandler={userInfoHanlder}
                inputProps={{
                  maxLength: 20,
                  css: css`
                    width: 200px;
                  `,
                }}
              />
              <FormInput
                id="passwordConfirm"
                type="password"
                placeholder="비밀번호 확인"
                infoState={infoState.passwordConfirm}
                disabled={!userInfo.password}
                inputHandler={userInfoHanlder}
                inputProps={{
                  maxLength: 20,
                  css: css`
                    width: 200px;
                  `,
                }}
              />
            </FormRow>
          </FormSection>

          <FormSection title="세무사 정보">
            <FormRow>
              <FormInput
                id="name"
                type="text"
                placeholder="이름"
                inputHandler={userInfoHanlder}
                inputProps={{
                  maxLength: 10,
                }}
              />
            </FormRow>
            <FormRow>
              <FormInput
                id="taxAgentNumber"
                type="text"
                placeholder="세무대리인 관리번호"
                inputProps={{ minLength: 4, maxLength: 8 }}
                value={userInfo.taxAgentNumber}
                inputHandler={userInfoHanlder}
                // button={{
                //   label: "중복확인",
                //   action: () => console.log("중복확인"),
                // }}
              />
              <FormInput
                id="taxOfficeCertFile"
                type="file"
                name="taxType"
                label="세무사/세무대리업무 등록증 파일첨부"
                labelCss={`
                  padding: 8px 16px;
                  background-color: ${
                    userInfo.taxAgentNumber
                      ? PALETTE.primary
                      : PALETTE.backgroundDisabled
                  };
                  border-radius: 8px;
                  color: ${PALETTE.light};
                `}
                placeholder="세무사 등록증"
                disabled={!userInfo.taxAgentNumber}
                infoState={infoState.taxOfficeCertFile}
                inputProps={{
                  variant: "standard",
                  accept: ".pdf, .jpg, .jpeg, .png",
                  css: css`
                    display: none;
                  `,
                }}
                inputHandler={fileHandler}
              />
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  display: ${userInfo.taxOfficeCertFile ? "flex" : "none"};
                `}
              >
                <span
                  css={css`
                    max-width: 200px;
                    margin: 0 4px;
                    font-size: 14px;
                    word-break: break-all;
                  `}
                >
                  {userInfo.taxOfficeCertFile}
                </span>
                <IconButton onClick={deleteCertFileRecord}>
                  <CloseIcon />
                </IconButton>
              </div>
            </FormRow>
            <FormRow>
              <FormInput
                id="mobileNo"
                type="tel"
                placeholder="휴대폰 번호"
                value={userInfo.mobileNo}
                infoState={infoState.mobileNo}
                inputHandler={userInfoHanlder}
                inputProps={{
                  maxLength: 13,
                }}
                button={{
                  label: "인증요청",
                  disabled:
                    !infoState.mobileNo.isValid ||
                    infoState.mobileNo.isLoading ||
                    Boolean(veriData.mobile.id),
                  action: () =>
                    requestVerification("mobile", "mobileVeriCode", "mobileNo"),
                }}
              />
              <FormInput
                id="mobileVeriCode"
                type="text"
                placeholder="휴대폰 인증번호"
                disabled={!veriData.mobile.id}
                value={userInfo.mobileVeriCode}
                infoState={infoState.mobileVeriCode}
                inputHandler={userInfoHanlder}
                inputProps={{
                  maxLength: 6,
                }}
                button={{
                  label: "인증확인",
                  disabled: !(
                    veriData.mobile.id && infoState.mobileVeriCode.isValid
                  ),
                  action: () =>
                    checkVeriCode("mobile", "mobileVeriCode", "mobileNo"),
                }}
              />
            </FormRow>
          </FormSection>

          <FormSection title="사무실 정보">
            <FormRow>
              <FormInput
                id="businessName"
                type="text"
                placeholder="사무실명"
                inputHandler={userInfoHanlder}
                inputProps={{
                  maxLength: 30, // 띄어쓰기와 병기된 괄호, 영어 글자까지 포함하여 상호 표기가능 글자수는 30글자
                }}
              />
              <FormInput
                id="businessRegNumber"
                type="text"
                placeholder="사업자등록번호"
                value={userInfo.businessRegNumber}
                infoState={infoState.businessRegNumber}
                inputHandler={userInfoHanlder}
                inputProps={{
                  maxLength: 12,
                }}
              />
              <FormInput
                id="businessCeoName"
                type="text"
                placeholder="대표자명"
                inputHandler={userInfoHanlder}
                inputProps={{
                  maxLength: 10,
                }}
              />
            </FormRow>
            <FormRow>
              <h5
                css={css`
                  font-size: 14px;
                `}
              >
                사업자 유형
              </h5>
              <FormInput
                id="bt-corporate"
                name="businessType"
                type="radio"
                disabled={userInfo.taxType === "simple"}
                label="법인사업자"
                value="corporate"
                inputHandler={userInfoHanlder}
                inputProps={{
                  defaultChecked: true,
                  css: inputRadioCss,
                }}
              />
              <FormInput
                id="bt-private"
                name="businessType"
                type="radio"
                label="개인사업자"
                value="private"
                inputHandler={userInfoHanlder}
                inputProps={{
                  css: inputRadioCss,
                }}
              />
            </FormRow>
            <FormRow>
              <h5
                css={css`
                  font-size: 14px;
                `}
              >
                과세 유형
              </h5>
              <FormInput
                id="tt-normal"
                name="taxType"
                type="radio"
                label="일반사업자"
                value="normal"
                inputHandler={userInfoHanlder}
                inputProps={{
                  defaultChecked: true,
                  css: inputRadioCss,
                }}
              />
            </FormRow>
            <FormRow>
              <FormInput
                id="taxEmail"
                type="email"
                placeholder="세금계산서 이메일"
                value={userInfo.taxEmail}
                infoState={infoState.taxEmail}
                inputHandler={userInfoHanlder}
                inputProps={{
                  css: css`
                    margin: 0;
                  `,
                }}
              />
            </FormRow>
          </FormSection>

          {/* <FormSection title="급여정보">
            <FormRow>
              <FormInput
                id="salaryMonth"
                type="text"
                placeholder="급여지급월"
                inputHandler={userInfoHanlder}
              />
              <FormInput
                id="salaryDay"
                type="text"
                placeholder="급여지급일"
                inputHandler={userInfoHanlder}
              />
            </FormRow>
          </FormSection> */}

          <FormSection title="약관">
            <div>
              <div
                css={css`
                  margin-bottom: 8px;
                  font-size: 14px;
                `}
              >
                가입시 아래에 대해 동의한 것으로 간주합니다.
              </div>
              <BaseButton
                color={PALETTE.primary}
                onClick={() => openTermDialog("privacy")}
                css={css`
                  padding: 0;
                  font-size: 14px;
                `}
              >
                개인정보처리방침
              </BaseButton>
              <TermDivider />
              <BaseButton
                color={PALETTE.primary}
                onClick={() => openTermDialog("consignment")}
                css={css`
                  padding: 0;
                  font-size: 14px;
                `}
              >
                개인정보처리위탁동의서
              </BaseButton>
              <TermDivider />
              <BaseButton
                color={PALETTE.primary}
                onClick={() => openTermDialog("service")}
                css={css`
                  padding: 0;
                  font-size: 14px;
                `}
              >
                서비스이용약관
              </BaseButton>
            </div>
            <TermDialog
              isOpen={Boolean(termDialogOpen)}
              setIsOpen={setTermDialogOpen}
              termType={termDialogOpen}
            />
          </FormSection>

          <BaseButton
            type="submit"
            color={PALETTE.primary}
            variant="contained"
            disabled={
              isLoading ||
              !(
                infoState.verification.isValid &&
                infoState.mobileVeriCode.isValid &&
                checkAllValid()
              )
            }
          >
            {convertButtonText(infoState, isLoading)}
          </BaseButton>
        </form>
      </div>
      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertDialogData}
      />
    </div>
  );
}

const TermDivider = () => {
  return (
    <span
      css={css`
        margin: 0 12px;
        font-size: 14px;
        color: ${PALETTE.primary};
      `}
    >
      |
    </span>
  );
};

const FormSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <section
      css={css`
        display: flex;
        flex-direction: column;
        gap: 24px;
      `}
    >
      <h4>{title}</h4>
      {children}
    </section>
  );
};

const FormRow = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 12px;
      `}
    >
      {children}
    </div>
  );
};

const FormInput = ({
  id,
  type,
  name,
  value,
  label,
  labelCss,
  placeholder,
  infoState,
  disabled,
  inputProps,
  button,
  inputHandler,
}: FormInputProps) => {
  return (
    <div>
      {label && !name && (
        <label
          htmlFor={id}
          css={css`
            display: block;
            ${labelCss}
          `}
        >
          {label}
        </label>
      )}
      <div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${name ? "8px" : "12px"};
          `}
        >
          <BaseInput
            id={id}
            name={name}
            type={type}
            placeholder={placeholder}
            disabled={disabled || infoState?.isDisabled}
            autoComplete="new-password"
            variant={inputProps?.variant || "outlined"}
            {...inputProps}
            value={value}
            onInput={inputHandler}
          />
          {name && label && (
            <label
              htmlFor={id}
              css={css`
                font-size: 14px;
                ${labelCss}
                ${disabled
                  ? `color: ${PALETTE.textDisabled};`
                  : type === "file" && `cursor: pointer;`}
              `}
            >
              {label}
            </label>
          )}
          {button && (
            <BaseButton
              variant="contained"
              disabled={
                button.disabled ||
                (infoState &&
                  (infoState.isLoading || infoState.isDisabled || !value))
              }
              color={PALETTE.primary}
              onClick={button.action}
            >
              {button.label}
            </BaseButton>
          )}
        </div>
        <div
          css={css`
            position: relative;
          `}
        >
          <div
            css={css`
              position: absolute;
              top: 2px;
              left: 2px;
              font-size: 11px;
              color: ${PALETTE.error};
            `}
          >
            {infoState?.errMsg}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
